import { useCallback, useEffect, useMemo, useState } from "react";
import { postFeedback } from "../../api/feedback";
import { postSSE } from "../../api/sse";
import { fetchThread } from "../../api/threads";
import { Markdown, MarkdownInline } from "../../components/Markdown";
import { useReport } from "../report/ReportContext";
import { ChartCard } from "../charts/ChartCard";
import type { ChartSpec } from "../charts/types";
import { api } from "../../api/base";

// Human-friendly past-tense labels for the collapsed "Thinking process"
// trace. The internal tool name is an implementation detail; the user
// reads a step like "Ran a query against the data warehouse" instead.
const PAST_TENSE: Record<string, string> = {
  execute_sql: "Ran a query against the data",
  render_chart: "Drew a chart",
  finalize_answer: "Wrapped up the answer",
  get_datasource_schema: "Looked up the schema",
  get_prompt_examples: "Looked up similar past queries",
  log_feedback: "Recorded your feedback",
};
function pastTenseLabel(name: string): string {
  return PAST_TENSE[name] ?? name;
}

/**
 * Plain-English description of what a tool call actually did, so the
 * collapsed "Thinking process" panel reads like a story instead of a
 * dump of JSON. Each branch returns ONE sentence based on the input
 * payload — no tool names, no SQL.
 */
function describeToolCall(name: string, input: Record<string, unknown>): string {
  if (name === "execute_sql") {
    const raw = String(input.raw_query ?? "").trim();
    return raw ? `Looked up data to answer "${truncate(raw, 90)}"` : "Ran a query against the data warehouse";
  }
  if (name === "render_chart") {
    const tpl = String(input.template_name ?? "");
    const title = String(input.title ?? "").trim();
    const shape = tpl
      ? tpl.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toLowerCase())
      : "custom";
    if (title) return `Drew a ${shape} chart titled "${title}"`;
    return `Drew a ${shape} chart`;
  }
  if (name === "get_datasource_schema") {
    // SSE tool_call previews replace list payloads with "<array len=N>"
    // for wire size; guard against that here instead of trusting the
    // type cast.
    const raw = input.dataset_names;
    if (Array.isArray(raw) && raw.length > 0) {
      return `Loaded the schema for ${joinNames(raw.map(String))}`;
    }
    const q = String(input.query ?? "").trim();
    if (q) return `Looked up schema details for "${truncate(q, 80)}"`;
    return "Loaded the datasource schema";
  }
  if (name === "get_prompt_examples") {
    const q = String(input.query ?? "").trim();
    return q ? `Looked up examples similar to "${truncate(q, 80)}"` : "Looked up similar past queries";
  }
  if (name === "finalize_answer") {
    return "Wrapped up the answer";
  }
  if (name === "log_feedback") {
    return "Recorded your feedback";
  }
  return pastTenseLabel(name);
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function joinNames(names: string[]): string {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

// The bearer used by all middleware fetches arrives via props from
// App.tsx; production is the Agami access_token minted by the OAuth
// flow, localhost without a session falls back to a dev bypass token
// the middleware recognises when APP_ENV=local.

interface ToolCallEntry {
  id: string;
  name: string;
  input: Record<string, unknown>;
  status: "running" | "ok" | "error";
  startedAt: number;
  elapsedMs: number;
}

interface FinalAnswer {
  text: string;
  insights: string[];
  follow_ups: string[];
  chart_ids: string[];
}

interface CompletedTurn {
  question: string;
  toolCalls: ToolCallEntry[];
  charts: ChartSpec[];
  proseText: string;
  final: FinalAnswer | null;
  error: string | null;
}

interface HistoryMessage {
  role: "user" | "assistant";
  content: string;
}

interface ChatProps {
  bearer: string;
  organization: string;
  dryRun: boolean;
  activeThreadId: number | null;
  // When set (via ?message=N), the replayed thread is filtered to
  // just that one chat_message — used by admin's "View Response"
  // deep-link to isolate a single turn from the full conversation.
  focusMessageId?: number | null;
  resetSignal: number;
  onThreadStarted: (threadId: number) => void;
  onContextLockChange: (locked: boolean) => void;
  onContextSync: (organization: string) => void;
}

export function Chat({
  bearer,
  organization,
  dryRun,
  activeThreadId,
  focusMessageId = null,
  resetSignal,
  onThreadStarted,
  onContextLockChange,
  onContextSync,
}: ChatProps) {
  const [message, setMessage] = useState("");
  const [running, setRunning] = useState(false);
  const [thinking, setThinking] = useState("");
  // Prose answer streamed from the most recent iteration's text deltas.
  // When `final` fires, this text is what the user sees as the answer.
  const [proseText, setProseText] = useState("");
  const [proseIteration, setProseIteration] = useState(0);
  const [toolCalls, setToolCalls] = useState<ToolCallEntry[]>([]);
  const [charts, setCharts] = useState<ChartSpec[]>([]);
  const [final, setFinal] = useState<FinalAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [turns, setTurns] = useState<CompletedTurn[]>([]);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [currentThreadId, setCurrentThreadId] = useState<number | null>(null);

  // Lock the context dropdowns in the header whenever we're mid-turn
  // or showing an existing thread, so the model's scope can't change
  // out from under the conversation.
  useEffect(() => {
    onContextLockChange(turns.length > 0 || pendingQuestion !== null);
  }, [turns.length, pendingQuestion, onContextLockChange]);

  const startConversation = useCallback(() => {
    setTurns([]);
    setMessage("");
    setThinking("");
    setProseText("");
    setProseIteration(0);
    setToolCalls([]);
    setCharts([]);
    setFinal(null);
    setError(null);
    setPendingQuestion(null);
    setCurrentThreadId(null);
  }, []);

  useEffect(() => {
    if (resetSignal === 0) return;
    startConversation();
  }, [resetSignal, startConversation]);

  useEffect(() => {
    if (activeThreadId == null) return;
    if (activeThreadId === currentThreadId) return;
    let cancelled = false;
    fetchThread(bearer, activeThreadId)
      .then((thread) => {
        if (cancelled) return;
        setCurrentThreadId(thread.id);
        // Sync the header context to the thread's org so the labels
        // match what's on screen. Datasource isn't stored per-thread
        // today; the user can pick it if they continue the conversation.
        onContextSync(thread.organization_name);
        const replayed = focusMessageId
          ? thread.messages.filter((m) => m.id === focusMessageId)
          : thread.messages;
        const reconstructed: CompletedTurn[] = replayed.map((m) => {
          const f = m.assistant_response.final;
          const proseText = f?.text ?? "";
          return {
            question: m.user_query,
            toolCalls: [],
            charts: m.assistant_response.charts ?? [],
            proseText,
            final: f
              ? {
                  text: f.text,
                  insights: f.insights ?? [],
                  follow_ups: f.follow_ups ?? [],
                  chart_ids: f.chart_ids ?? [],
                }
              : null,
            error: null,
          };
        });
        setTurns(reconstructed);
        setPendingQuestion(null);
        setMessage("");
        setThinking("");
        setProseText("");
        setProseIteration(0);
        setToolCalls([]);
        setCharts([]);
        setFinal(null);
        setError(null);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [activeThreadId, currentThreadId, focusMessageId, onContextSync]);

  const runQuery = useCallback(
    async (rawQuestion: string) => {
      const question = rawQuestion.trim();
      if (!question || running) return;

      const history: HistoryMessage[] = [];
      for (const t of turns) {
        history.push({ role: "user", content: t.question });
        if (t.final) history.push({ role: "assistant", content: t.final.text });
      }

      setRunning(true);
      setPendingQuestion(question);
      setMessage("");
      setThinking("");
      setProseText("");
      setProseIteration(0);
      setToolCalls([]);
      setCharts([]);
      setFinal(null);
      setError(null);

      let liveTools: ToolCallEntry[] = [];
      let liveCharts: ChartSpec[] = [];
      let liveFinal: FinalAnswer | null = null;
      let liveError: string | null = null;
      let liveProse = "";
      let liveProseIter = 0;

      try {
        for await (const frame of postSSE({
          url: api("/chat"),
          headers: { Authorization: `Bearer ${bearer}` },
          body: {
            organization,
            message: question,
            history,
            thread_id: currentThreadId,
            dry_run: dryRun,
          },
        })) {
          const data = frame.data as Record<string, unknown>;
          switch (frame.event) {
            case "thread": {
              const tid = Number(data.thread_id);
              if (Number.isFinite(tid)) {
                setCurrentThreadId(tid);
                onThreadStarted(tid);
              }
              break;
            }
            case "thinking":
              setThinking((t) => t + String(data.text ?? ""));
              break;
            case "text_delta": {
              const iter = Number(data.iteration ?? 0);
              const piece = String(data.text ?? "");
              if (iter !== liveProseIter) {
                // New iteration -> previous iteration's text was
                // intermediate reasoning; archive it as thinking
                // (collapsed in the trace) and start fresh.
                if (liveProse) {
                  setThinking((t) => (t ? t + "\n\n" + liveProse : liveProse));
                }
                liveProseIter = iter;
                liveProse = piece;
              } else {
                liveProse = liveProse + piece;
              }
              setProseText(liveProse);
              setProseIteration(liveProseIter);
              break;
            }
            case "tool_call": {
              const entry: ToolCallEntry = {
                id: String(data.id),
                name: String(data.name),
                input: (data.input as Record<string, unknown>) ?? {},
                status: "running",
                startedAt: Date.now(),
                elapsedMs: 0,
              };
              liveTools = [...liveTools, entry];
              setToolCalls(liveTools);
              break;
            }
            case "heartbeat": {
              liveTools = liveTools.map((c) =>
                c.id === data.id ? { ...c, elapsedMs: Number(data.elapsed_ms ?? 0) } : c,
              );
              setToolCalls(liveTools);
              break;
            }
            case "tool_result": {
              liveTools = liveTools.map((c) =>
                c.id === data.id
                  ? { ...c, status: data.ok ? "ok" : "error", elapsedMs: Date.now() - c.startedAt }
                  : c,
              );
              setToolCalls(liveTools);
              break;
            }
            case "chart": {
              liveCharts = [...liveCharts, data as unknown as ChartSpec];
              setCharts(liveCharts);
              break;
            }
            case "final": {
              // Backfill `text` from the streamed prose at the moment
              // finalize_answer fires. Without this, the live prose
              // hides (live && proseText && !final → false) but the
              // AnswerBody renders nothing because the model didn't
              // pass `text` — the user sees the answer disappear.
              const modelText = String(data.text ?? "");
              liveFinal = {
                text: modelText || liveProse,
                insights: (data.insights as string[]) ?? [],
                follow_ups: (data.follow_ups as string[]) ?? [],
                chart_ids: (data.chart_ids as string[]) ?? [],
              };
              setFinal(liveFinal);
              break;
            }
            case "error":
              liveError = `${data.kind}: ${data.message}`;
              setError(liveError);
              break;
            case "done":
              break;
          }
        }
      } catch (e) {
        liveError = e instanceof Error ? e.message : String(e);
        setError(liveError);
      } finally {
        // Use the streamed prose as the answer if the model didn't
        // emit a `text` field in finalize_answer (the new pattern).
        if (liveFinal && !liveFinal.text && liveProse) {
          liveFinal = { ...liveFinal, text: liveProse };
        }
        // Defensive backstop for end_turn-without-finalize. The
        // orchestrator now synthesizes a final event in this case
        // (see agent_loop.py), so this path rarely fires — but if a
        // stream ends with prose and no final at all (older servers,
        // network drop after text_delta but before final), promote
        // the prose to a real answer so the user doesn't see a blank.
        if (!liveFinal && liveProse) {
          liveFinal = { text: liveProse, insights: [], follow_ups: [], chart_ids: [] };
        }
        setTurns((prev) => [
          ...prev,
          {
            question,
            toolCalls: liveTools,
            charts: liveCharts,
            proseText: liveProse,
            final: liveFinal,
            error: liveError,
          },
        ]);
        setPendingQuestion(null);
        setThinking("");
        setProseText("");
        setProseIteration(0);
        setToolCalls([]);
        setCharts([]);
        setFinal(null);
        setError(null);
        setRunning(false);
      }
    },
    [bearer, organization, dryRun, running, turns, currentThreadId, onThreadStarted],
  );

  const submit = useCallback(() => {
    void runQuery(message);
  }, [runQuery, message]);

  const handleFollowUp = useCallback(
    (q: string) => {
      setMessage("");
      void runQuery(q);
    },
    [runQuery],
  );

  const examples = useMemo(
    () => exampleQuestions(organization),
    [organization],
  );

  const showEmptyState = turns.length === 0 && !pendingQuestion;

  return (
    <>
      {showEmptyState ? (
        <div className="flex-1 flex flex-col justify-center -mt-16">
          <ComposeBar
            message={message}
            running={running}
            placeholder="Type your query here.."
            onChange={setMessage}
            onSubmit={submit}
          />
          <EmptyState examples={examples} onPick={handleFollowUp} />
        </div>
      ) : (
        <>
          <div className="flex-1 space-y-10 pb-32">
            {turns.map((turn, i) => (
              <TurnView
                key={i}
                bearer={bearer}
                question={turn.question}
                organization={organization}
                toolCalls={turn.toolCalls}
                charts={turn.charts}
                proseText={turn.proseText}
                final={turn.final}
                error={turn.error}
                onFollowUp={handleFollowUp}
              />
            ))}

            {pendingQuestion && (
              <TurnView
                bearer={bearer}
                question={pendingQuestion}
                organization={organization}
                thinking={thinking}
                toolCalls={toolCalls}
                charts={charts}
                proseText={proseText}
                proseIteration={proseIteration}
                final={final}
                error={error}
                live
                onFollowUp={handleFollowUp}
              />
            )}
          </div>
          <div className="sticky bottom-6 z-10">
            <ComposeBar
              message={message}
              running={running}
              placeholder="Type your query here.."
              onChange={setMessage}
              onSubmit={submit}
            />
          </div>
        </>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Empty-state question list (matches "Questions you can ask").        */
/* ------------------------------------------------------------------ */

function exampleQuestions(org: string): string[] {
  // Per-org suggestions only — the model picks the right datasource
  // per question, so we don't need datasource-scoped lists anymore.
  if (org === "MeridianDemo") {
    return [
      "How many accounts do we have, grouped by industry?",
      "Show the deal pipeline by stage with total value and count.",
      "How many incidents do we have by priority and status?",
      "Which assignment groups have the longest mean resolution time this month?",
      "Build me a dashboard: accounts by industry, pipeline by stage, top 10 accounts by deal value.",
    ];
  }
  if (org === "SunMobility") {
    return [
      "How has the daily alert volume trended over the last 30 days?",
      "Show the daily trend of swap counts by station, admin, and ESV over the last 30 days",
      "How many battery packs have not been swapped in the last 7 days?",
      "What is the current SOC and cycle count for battery INSMOMAH2304A0200A11?",
      "What are the top 5 quarantine reasons across the fleet?",
    ];
  }
  if (org === "FinBud") {
    return [
      "How many borrowers do we have, grouped by loan product?",
      "What's the average DPD across personal loan borrowers?",
      "Show the distribution of credit scores in our portfolio.",
    ];
  }
  if (org === "HomeMedicalProvider") {
    return [
      "What was our revenue by device category last quarter?",
      "Break down revenue by payer type.",
      "Which region grew the fastest year over year?",
      "What did we bill versus what we actually collected by payer, and where is the leakage?",
      "Build me a dashboard: revenue by device category, billed vs collected by payer, and DSO by region.",
    ];
  }
  return [];
}

interface EmptyStateProps {
  examples: string[];
  onPick: (q: string) => void;
}

function EmptyState({ examples, onPick }: EmptyStateProps) {
  if (examples.length === 0) return null;
  return (
    <div className="mt-10 max-w-2xl mx-auto w-full">
      <div className="text-[12px] uppercase tracking-[0.08em] text-neutral-400 font-semibold mb-3 px-1">
        Questions you can ask
      </div>
      <div className="grid gap-2.5 sm:grid-cols-2">
        {examples.map((q, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onPick(q)}
            className="group text-left px-4 py-3 rounded-[18px] bg-chip/60 hover:bg-chip text-[14px] text-neutral-800 hover:text-neutral-900 leading-snug transition-all hover:-translate-y-0.5 hover:shadow-sm border border-transparent hover:border-line flex items-start gap-2"
          >
            <span className="flex-1">{q}</span>
            <span className="text-neutral-300 group-hover:text-brand transition-colors shrink-0">
              <ArrowRightIcon />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ArrowRightIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4 mt-0.5" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 3l5 5-5 5M5 8h6" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Compose bar — pill shape matching web/.wrapper-txt-ai-search.       */
/* ------------------------------------------------------------------ */

interface ComposeBarProps {
  message: string;
  running: boolean;
  placeholder: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
}

function ComposeBar({ message, running, placeholder, onChange, onSubmit }: ComposeBarProps) {
  return (
    <div className="flex items-center gap-2 rounded-pill border border-line bg-white p-1.5 shadow-card focus-within:shadow-focus transition-shadow">
      <textarea
        className="flex-1 resize-none px-4 py-2 text-[14px] bg-transparent outline-none placeholder:text-neutral-400 max-h-32"
        placeholder={placeholder}
        rows={1}
        value={message}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSubmit();
          }
        }}
      />
      <button
        type="button"
        onClick={onSubmit}
        disabled={running || !message.trim()}
        className="btn-primary h-9 px-5"
      >
        {running ? <Spinner /> : null}
        {running ? "Running" : "Ask"}
      </button>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* One conversation turn.                                              */
/* ------------------------------------------------------------------ */

interface TurnViewProps {
  bearer: string;
  question: string;
  organization: string;
  thinking?: string;
  toolCalls: ToolCallEntry[];
  charts: ChartSpec[];
  proseText?: string;
  proseIteration?: number;
  final: FinalAnswer | null;
  error: string | null;
  live?: boolean;
  onFollowUp: (q: string) => void;
}

function TurnView({
  bearer,
  question,
  organization,
  thinking = "",
  toolCalls,
  charts,
  proseText = "",
  final,
  error,
  live = false,
  onFollowUp,
}: TurnViewProps) {
  const complete = final !== null || error !== null;
  return (
    <section className="space-y-5">
      <UserBubble text={question} />

      {/* When the turn is done, show the collapsed "Thinking process"
          panel. While live we hide it entirely — the ActivityIndicator
          below owns all live status. */}
      {!live && (toolCalls.length > 0 || thinking.length > 0) && (
        <ThinkingPanel thinking={thinking} toolCalls={toolCalls} />
      )}

      {/* Live streaming prose: appears as soon as the model starts
          writing its answer. Rendered raw (no auto-highlight) for
          smoother streaming; the final answer below upgrades to full
          markdown once the turn completes. */}
      {live && proseText && !final && <StreamingProse text={proseText} />}

      {/* Always-on activity indicator while the turn is live. Owns the
          initial "Thinking…" frame, per-tool labels with elapsed time,
          and the between-iteration "Working…" gap. */}
      {live && !complete && (
        <ActivityIndicator toolCalls={toolCalls} hasProse={proseText.length > 0} />
      )}

      {charts.map((spec) => (
        <ChartCard
          key={spec.chart_id}
          spec={spec}
          bearer={bearer}
          organization={organization}
          userQuery={question}
        />
      ))}

      {final && (
        <AnswerBody
          bearer={bearer}
          final={final}
          organization={organization}
          rawQuery={question}
          onFollowUp={onFollowUp}
        />
      )}

      {error && <ErrorBanner kind={error} />}
    </section>
  );
}

/**
 * Streaming-phase prose renderer. Uses the full Markdown component so
 * headings, lists, tables, and horizontal rules render as they arrive
 * (the model often glues a heading and its bullets together with no
 * blank line, and emits --- separators between sections). The
 * `streaming` flag skips the per-chunk auto-highlight regex pass —
 * AnswerBody re-renders without it once the turn closes and picks up
 * currency/date/percentage bolds.
 */
function StreamingProse({ text }: { text: string }) {
  return <Markdown text={text} className="text-[15px] text-neutral-900 leading-relaxed" streaming />;
}

/* ------------------------------------------------------------------ */
/* Persistent "still working" indicator under the streamed prose.      */
/* ------------------------------------------------------------------ */

function ActivityIndicator({
  toolCalls,
  hasProse,
}: {
  toolCalls: ToolCallEntry[];
  hasProse: boolean;
}) {
  const [now, setNow] = useState(() => Date.now());
  const running = toolCalls.find((c) => c.status === "running");
  // Tick once a second whenever the turn is live so the elapsed counter
  // updates even if the model is between tool calls.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);

  const elapsedMs = running ? Math.max(running.elapsedMs, now - running.startedAt) : 0;
  const elapsed = running ? formatElapsedShort(elapsedMs) : "";

  let label: string;
  if (running) {
    label = activityLabel(running.name, running.input, elapsedMs);
  } else if (hasProse) {
    label = "Writing the answer";
  } else if (toolCalls.length === 0) {
    label = "Thinking";
  } else {
    // A tool just finished; the model is deciding the next step.
    label = "Working on the next step";
  }

  return (
    <div className="flex items-center gap-2 text-[13px] text-neutral-500 italic">
      <RingSpinner />
      <span>
        {label}
        <DotPulse />
        {elapsed && (
          <span className="ml-2 tabular-nums text-neutral-400 not-italic">{elapsed}</span>
        )}
      </span>
    </div>
  );
}

function DotPulse() {
  // Three-dot loader baked into the indicator label so the user
  // always sees motion, even when the elapsed counter isn't ticking
  // (between iterations / before the first tool runs).
  return (
    <span className="inline-flex ml-1 items-center gap-[2px] not-italic align-middle">
      <span className="dot-pulse" style={{ animationDelay: "0ms" }} />
      <span className="dot-pulse" style={{ animationDelay: "160ms" }} />
      <span className="dot-pulse" style={{ animationDelay: "320ms" }} />
    </span>
  );
}

/** Per-tool human-friendly progress label that evolves with elapsed time
 *  so the user sees more than the same "Querying data" stamp for 30s.
 */
function activityLabel(name: string, input: Record<string, unknown>, elapsedMs: number): string {
  const secs = Math.floor(elapsedMs / 1000);
  if (name === "execute_sql") {
    if (secs < 2) return "Writing the SQL query";
    if (secs < 8) return "Running the query";
    if (secs < 30) return "Fetching results from the database";
    if (secs < 90) return "Still fetching — this is a heavy query";
    return "Waiting on the database (large scan)";
  }
  if (name === "render_chart") {
    const tpl = input.template_name ? String(input.template_name) : "custom";
    return tpl === "custom" ? "Designing a custom chart" : `Drawing the ${tpl.replace("_", " ")} chart`;
  }
  if (name === "get_datasource_schema") {
    const raw = input.dataset_names;
    if (Array.isArray(raw) && raw.length > 0) {
      return `Looking up details for ${raw.map(String).join(", ")}`;
    }
    return "Loading the schema for this datasource";
  }
  if (name === "get_prompt_examples") {
    return "Looking up similar past queries";
  }
  if (name === "finalize_answer") {
    return "Wrapping up the answer";
  }
  if (name === "log_feedback") {
    return "Recording your feedback";
  }
  return pastTenseLabel(name);
}

function ErrorBanner({ kind }: { kind: string }) {
  // `kind` arrives as "rate_limit: <msg>" from the server. Render
  // friendly copy for known kinds; fall back to the raw message.
  const m = /^(\w+):\s*(.*)$/.exec(kind);
  const isRate = m && m[1] === "rate_limit";
  const isOverload = m && m[1] === "overloaded";
  if (isRate || isOverload) {
    const body = m ? m[2] : kind;
    return (
      <div className="rounded-[15px] border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <div className="font-medium mb-1">
          {isRate ? "Hit the model's rate limit" : "Model overloaded"}
        </div>
        <div className="text-amber-800/90">{body}</div>
      </div>
    );
  }
  return (
    <div className="rounded-[15px] border border-red-200 bg-red-50 p-4 text-sm text-red-800">
      {kind}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Right-aligned user question bubble.                                 */
/* ------------------------------------------------------------------ */

function UserBubble({ text }: { text: string }) {
  return (
    <div className="flex justify-end">
      <div
        className="bg-chip text-neutral-900 px-5 py-2.5 max-w-[80%] font-medium text-[14px]"
        style={{ borderRadius: "23px 0 23px 23px" }}
      >
        {text}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Thinking panel — live trace + collapsed "Thinking process" when done. */
/* ------------------------------------------------------------------ */

interface ThinkingPanelProps {
  thinking: string;
  toolCalls: ToolCallEntry[];
}

/**
 * Collapsed history of what the model did on this turn. Only rendered
 * once the turn finishes — live state is owned entirely by
 * ActivityIndicator. The expanded view describes each step in plain
 * English ("Ran a query against the data" / "Drew a bar chart titled
 * X") rather than dumping tool names + SQL on the user.
 */
function ThinkingPanel({ thinking, toolCalls }: ThinkingPanelProps) {
  const [open, setOpen] = useState(false);
  if (thinking.length === 0 && toolCalls.length === 0) return null;

  // Skip render_chart from the user-visible step count — it's an
  // implementation detail of how charts get to the screen, not a
  // separate "I did this thing" worth surfacing.
  const visibleSteps = toolCalls.filter((c) => c.name !== "render_chart" && c.name !== "finalize_answer");

  return (
    <div className="rounded-[15px] border border-line overflow-hidden bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-chip transition-colors"
      >
        <CheckIcon />
        <span className="text-[13px] font-medium text-neutral-800">Thinking process</span>
        {visibleSteps.length > 0 && (
          <span className="chip text-[11px]">
            {visibleSteps.length} {visibleSteps.length === 1 ? "step" : "steps"}
          </span>
        )}
        <span className="ml-auto text-neutral-400 text-xs">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="px-4 py-3 space-y-2 border-t border-line bg-white">
          {thinking && (
            <div className="text-sm text-neutral-700 italic whitespace-pre-wrap leading-relaxed mb-1">
              {thinking}
            </div>
          )}
          <ol className="space-y-1.5">
            {visibleSteps.map((c, i) => (
              <li key={c.id} className="flex items-start gap-2.5 text-[13px] text-neutral-800">
                <span className="mt-[6px] h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                <span className="text-neutral-400 tabular-nums text-[12px] shrink-0 w-5">
                  {i + 1}.
                </span>
                <span className="flex-1">{describeToolCall(c.name, c.input)}</span>
                {c.elapsedMs > 0 && (
                  <span className="text-[11px] text-neutral-400 tabular-nums shrink-0">
                    {formatElapsed(c.elapsedMs)}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4 text-emerald-600">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 12.5L10 17.5L19.5 8" />
    </svg>
  );
}

function RingSpinner() {
  return (
    <svg className="animate-spin h-3.5 w-3.5 text-brand mt-1 shrink-0" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function formatElapsedShort(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

function formatElapsed(ms: number): string {
  if (ms <= 0) return "";
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.floor(s % 60)}s`;
}

/* ------------------------------------------------------------------ */
/* Final-answer body: insights, follow-ups, action row.                */
/* ------------------------------------------------------------------ */

interface AnswerBodyProps {
  bearer: string;
  final: FinalAnswer;
  organization: string;
  rawQuery: string;
  onFollowUp: (q: string) => void;
}

function AnswerBody({ bearer, final, organization, rawQuery, onFollowUp }: AnswerBodyProps) {
  return (
    <div className="space-y-6">
      {final.text && (
        <Markdown text={final.text} className="text-[15px] text-neutral-900 leading-relaxed" />
      )}

      {final.insights.length > 0 && (
        <ul className="space-y-1.5 text-[14px] text-neutral-800 pl-1">
          {final.insights.map((ins, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-neutral-400 mt-1.5 h-1 w-1 rounded-full bg-neutral-400 shrink-0" />
              <span>
                <MarkdownInline text={ins} />
              </span>
            </li>
          ))}
        </ul>
      )}

      {final.follow_ups.length > 0 && <FollowUps items={final.follow_ups} onPick={onFollowUp} />}

      <ActionRow
        bearer={bearer}
        organization={organization}
        rawQuery={rawQuery}
        answerText={final.text}
        insights={final.insights}
      />
    </div>
  );
}

function FollowUps({ items, onPick }: { items: string[]; onPick: (q: string) => void }) {
  return (
    <div className="space-y-1">
      <div className="text-[13px] text-neutral-500">Know more</div>
      <div className="flex flex-col">
        {items.map((q, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onPick(q)}
            className="flex items-start gap-2 text-left text-[14px] text-neutral-800 hover:text-brand py-1.5"
          >
            <SparkleIcon />
            <span>{q}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function SparkleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-4 w-4 text-brand mt-0.5 shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 3l1.5 3L9.5 7.5 6.5 9 5 12 3.5 9 0.5 7.5 3.5 6 5 3z" transform="translate(2 2)" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 12l1 2 2 1-2 1-1 2-1-2-2-1 2-1 1-2z" />
    </svg>
  );
}

/* Action row — thumbs / bug / share with gradient separators. */

interface ActionRowProps {
  bearer: string;
  organization: string;
  rawQuery: string;
  answerText: string;
  insights: string[];
}

function ActionRow({ bearer, organization, rawQuery, answerText, insights }: ActionRowProps) {
  const report = useReport();
  const [reportAdded, setReportAdded] = useState(false);
  const onAddToReport = () => {
    if (!answerText && insights.length === 0) return;
    report.addAnswer({
      title: deriveTitle(rawQuery),
      userQuery: rawQuery,
      text: answerText,
      insights,
    });
    setReportAdded(true);
    window.setTimeout(() => setReportAdded(false), 1500);
  };
  const [rating, setRating] = useState<"up" | "down" | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errored, setErrored] = useState(false);
  const [saved, setSaved] = useState(false);
  // Thumbs-down opens a comment box mirroring web/ behaviour:
  // first click reveals the box (no immediate POST), submit sends
  // {rating: 'bad', notes}. Re-clicking thumbs-down hides the box.
  const [downOpen, setDownOpen] = useState(false);
  const [notes, setNotes] = useState("");

  const flashSaved = useCallback(() => {
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1800);
  }, []);

  const postRating = useCallback(
    async (next: "up" | "down", extraNotes?: string) => {
      setSubmitting(true);
      setErrored(false);
      try {
        await postFeedback(bearer, {
          organization,
          raw_query: rawQuery,
          rating: next === "up" ? "good" : "bad",
          notes: extraNotes,
        });
        flashSaved();
        return true;
      } catch (e) {
        console.error("Feedback POST failed:", e);
        setErrored(true);
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [bearer, organization, rawQuery, flashSaved],
  );

  const onThumbUp = useCallback(async () => {
    if (rating === "up") {
      setRating(null);
      return;
    }
    setDownOpen(false);
    setRating("up");
    const ok = await postRating("up");
    if (!ok) setRating(null);
  }, [rating, postRating]);

  const onThumbDown = useCallback(() => {
    // Toggle the comment box. No POST until the user hits Send so
    // they can either submit with notes or back out.
    if (rating === "down") {
      setRating(null);
      setDownOpen(false);
      return;
    }
    setRating("down");
    setDownOpen(true);
  }, [rating]);

  const submitDownFeedback = useCallback(async () => {
    const trimmed = notes.trim();
    const ok = await postRating("down", trimmed || undefined);
    if (ok) {
      setDownOpen(false);
      setNotes("");
    } else {
      setRating(null);
    }
  }, [notes, postRating]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 pt-1">
        <div className="action-sep" />
        <button
          type="button"
          disabled={submitting}
          onClick={onThumbUp}
          className={"btn-white icon-only " + (rating === "up" ? "!text-emerald-600" : "")}
          aria-label="Good answer"
          title="Good answer"
        >
          <ThumbUpIcon />
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={onThumbDown}
          className={"btn-white icon-only " + (rating === "down" ? "!text-red-600" : "")}
          aria-label="Bad answer"
          title="Bad answer"
        >
          <ThumbDownIcon />
        </button>
        <button type="button" className="btn-white icon-only" aria-label="Report a bug" title="Report a bug">
          <BugIcon />
        </button>
        <button
          type="button"
          onClick={onAddToReport}
          className={"btn-white icon-only " + (reportAdded ? "!text-emerald-600" : "")}
          aria-label="Add to PDF report"
          title="Add to PDF report"
        >
          {reportAdded ? <CheckIcon /> : <ReportPlusIcon />}
        </button>
        {saved && <span className="text-[11px] text-emerald-600 ml-1">Saved</span>}
        {errored && <span className="text-[11px] text-red-500">couldn't save</span>}
        <div className="action-sep" />
      </div>

      {downOpen && (
        <div className="rounded-[15px] border border-line bg-white p-3 space-y-2">
          <label className="text-[12px] text-neutral-500" htmlFor="feedback-notes">
            What went wrong? (optional)
          </label>
          <textarea
            id="feedback-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. wrong column used, numbers don't match dashboard, missing breakdown by region…"
            rows={3}
            className="w-full text-[14px] bg-chip/40 rounded-md border border-line p-2.5 outline-none focus:ring-1 focus:ring-brand resize-none"
            autoFocus
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                void submitDownFeedback();
              }
            }}
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setDownOpen(false);
                setRating(null);
                setNotes("");
              }}
              className="btn-white h-8 px-3 text-[12px]"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={() => void submitDownFeedback()}
              className="btn-primary h-8 px-4 text-[12px]"
            >
              {submitting ? "Sending…" : "Send feedback"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ThumbUpIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-4 w-4">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 22V11M7 11l3-7c1.5 0 2.5 1 2.5 2.5V10h5.5a2 2 0 011.9 2.6l-2.2 7A2 2 0 0115.7 21H8a1 1 0 01-1-1z" />
    </svg>
  );
}

function ThumbDownIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-4 w-4">
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 2v11M17 13l-3 7c-1.5 0-2.5-1-2.5-2.5V14H6a2 2 0 01-1.9-2.6l2.2-7A2 2 0 018.3 3H16a1 1 0 011 1z" />
    </svg>
  );
}

function BugIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-4 w-4">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 4l1.5 1.5M15 4l-1.5 1.5M12 8a4 4 0 014 4v3a4 4 0 11-8 0v-3a4 4 0 014-4zM6 12H3M21 12h-3M6 16l-2 1M20 17l-2-1M6 8L4 7M20 7l-2 1" />
    </svg>
  );
}

function ReportPlusIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-4 w-4">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 4h7l4 4v8a1 1 0 01-1 1H7a1 1 0 01-1-1V5a1 1 0 011-1z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M14 4v4h4" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 11v6m-3-3h6" />
    </svg>
  );
}

// Derive a short, human-readable insight title from the user's NL question.
// Falls back to "Untitled insight" for empty queries (rare). Capitalises the
// first letter and trims at ~60 chars so the drawer row stays tidy.
function deriveTitle(rawQuery: string): string {
  const q = (rawQuery || "").trim();
  if (!q) return "Untitled insight";
  const cap = q.charAt(0).toUpperCase() + q.slice(1);
  return cap.length > 60 ? `${cap.slice(0, 57)}…` : cap;
}
